import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button, Menu, MenuItem, MenuList } from "@mui/material";
import { WorkspaceDocumentIcon, WorkspaceFormIcon } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { useIsViewerPermission } from "hooks";

import { RoleName } from "../../../constants";
import { useWorkspaceFolderHook } from "../hooks/use-workspace-folder-hook";

const panelMenuPaperSx = {
  borderRadius: "8px",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
  minWidth: 200,
  padding: "6px 0"
};

const panelMenuItemSx = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "15px",
  color: "#20293C",
  minHeight: 40,
  "&:hover": { backgroundColor: "#ECF1F8" }
};

export const AddNewMenu: React.FC = () => {
  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { onCreateNewFolder, onAddForm, onAddDocument, onFileUpload } = useWorkspaceFolderHook({
    folder: { id: -1, title: "", is_private: false, parent_id: null, deal_id: null, user_id: -1, kind: "folder" }
  });

  const onMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onMenuClose = () => {
    setAnchorEl(null);
  };

  const options = [
    {
      title: "New Folder",
      icon: <CreateNewFolderOutlinedIcon sx={{ fill: "#5d7782" }} />,
      onClick: () => {
        onMenuClose();
        onCreateNewFolder?.();
      },
      disabled: !onCreateNewFolder
    },
    {
      title: "New Document",
      icon: <WorkspaceDocumentIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
      onClick: () => {
        onMenuClose();
        onAddDocument?.();
      },
      disabled: !onAddDocument
    },
    {
      title: "New Form",
      icon: <WorkspaceFormIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
      onClick: () => {
        onMenuClose();
        onAddForm?.();
      },
      disabled: !onAddForm
    },
    {
      title: "Upload File",
      icon: <FileUploadOutlinedIcon sx={{ fill: "#5d7782" }} />,
      onClick: () => {
        onMenuClose();
        onFileUpload?.();
      },
      disabled: !onFileUpload
    }
  ];

  return (
    <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isAnalystDealsViewerPermission}>
      <Button
        onClick={onMenuOpen}
        variant="contained"
        startIcon={<AddIcon />}
        sx={{
          bgcolor: "#10212E",
          color: "#fff",
          fontSize: "16px",
          fontWeight: 400
        }}
      >
        Add New
      </Button>
      <>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={onMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: panelMenuPaperSx } }}
          // MenuListProps={{ sx: { padding: 0 } }}
        >
          <MenuList disablePadding>
            {options.map((option) => (
              <MenuItem key={option.title} onClick={option.onClick} disabled={option.disabled} sx={panelMenuItemSx}>
                {option.icon}
                {option.title}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </>
    </RoleComponentGuard>
  );
};
